import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, Layers, GripVertical, CreditCard, Download,
  ArrowRight, ArrowLeft, Sparkles, X, Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LogoMark } from '@/components/Logo';

const STORAGE_KEY = 'insurelab_onboarding_done';

const STEPS = [
  {
    id: 'welcome',
    icon: Sparkles,
    title: 'Welcome to InsureLab!',
    subtitle: 'Your AI-powered insurance builder',
    description: 'Build custom insurance policies in minutes, not weeks. Let\'s walk you through how it works.',
    visual: 'logo',
    highlight: null,
  },
  {
    id: 'ai-chat',
    icon: MessageSquare,
    title: 'Meet Your AI Advisor',
    subtitle: 'The chat panel on the right',
    description: 'Tell the AI about your business — industry, size, concerns. It will analyze your risks and suggest the perfect coverage blocks.',
    tip: 'Try: "I run a 20-person tech startup in Austin"',
    visual: 'chat',
    highlight: 'chat-sidebar',
  },
  {
    id: 'coverage',
    icon: Layers,
    title: 'Add Coverage Blocks',
    subtitle: 'Click suggestions or browse the library',
    description: 'When the AI suggests coverages, click to add them to your canvas. You can also browse all available blocks from the "Add Coverage" button.',
    visual: 'blocks',
    highlight: 'add-coverage',
  },
  {
    id: 'drag-drop',
    icon: GripVertical,
    title: 'Drag & Organize',
    subtitle: 'Rearrange your policy visually',
    description: 'Drag coverage blocks to reorder them. Your layout is automatically saved. This is your policy — arrange it however makes sense.',
    visual: 'drag',
    highlight: 'canvas',
  },
  {
    id: 'finalize',
    icon: CreditCard,
    title: 'Finalize & Pay',
    subtitle: 'Download PDF or checkout',
    description: 'When you\'re happy with your coverage, download a PDF document or finalize with secure Stripe checkout. Choose monthly or annual billing.',
    visual: 'checkout',
    highlight: 'toolbar',
  },
  {
    id: 'ready',
    icon: Rocket,
    title: 'You\'re All Set!',
    subtitle: 'Start building your first policy',
    description: 'That\'s it! Start by describing your business in the chat, and your AI advisor will guide you from there.',
    visual: 'ready',
    highlight: null,
  },
];

const StepVisual = ({ step }) => {
  const visuals = {
    logo: (
      <div className="relative w-full h-full flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.05, 1], rotate: [0, 2, -2, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
        >
          <LogoMark size={80} />
        </motion.div>
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
          style={{ background: 'radial-gradient(circle, rgba(0,174,239,0.15) 0%, transparent 70%)' }}
        />
      </div>
    ),
    chat: (
      <div className="w-full max-w-[260px] mx-auto space-y-3">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="bg-[#E8EEF4] rounded-2xl rounded-tl px-4 py-3 text-sm text-[#0D1B2A] max-w-[85%]">
          Hi! Tell me about your business...
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.8 }} className="bg-[#1A3A64] rounded-2xl rounded-tr px-4 py-3 text-sm text-white max-w-[85%] ml-auto">
          I run a tech startup, 20 employees
        </motion.div>
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.4 }} className="bg-[#E8EEF4] rounded-2xl rounded-tl px-4 py-3 text-sm text-[#0D1B2A] max-w-[85%]">
          <p>Great! I recommend:</p>
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2 px-2 py-1 bg-white rounded-lg text-xs">
              <span className="text-[#00AEEF]">+</span> Cyber Liability
            </div>
            <div className="flex items-center gap-2 px-2 py-1 bg-white rounded-lg text-xs">
              <span className="text-[#00AEEF]">+</span> Professional E&O
            </div>
          </div>
        </motion.div>
      </div>
    ),
    blocks: (
      <div className="grid grid-cols-2 gap-3 max-w-[280px] mx-auto">
        {['Cyber Liability', 'Professional E&O', 'General Liability', 'D&O'].map((name, i) => (
          <motion.div
            key={name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15 }}
            className="bg-white rounded-xl border border-[#DCE4ED] p-3 text-center"
          >
            <div className="w-8 h-8 bg-[#1A3A64]/10 rounded-lg flex items-center justify-center mx-auto mb-2">
              <Layers size={16} className="text-[#1A3A64]" />
            </div>
            <p className="text-xs font-medium text-[#0D1B2A]">{name}</p>
            <p className="text-[10px] text-[#00AEEF] font-medium mt-1">$150/mo</p>
          </motion.div>
        ))}
      </div>
    ),
    drag: (
      <div className="max-w-[260px] mx-auto space-y-2">
        {['General Liability', 'Cyber Liability', 'Professional E&O'].map((name, i) => (
          <motion.div
            key={name}
            initial={{ x: 0 }}
            animate={{ x: i === 1 ? [0, 30, -10, 0] : 0, y: i === 1 ? [0, -5, 5, 0] : 0 }}
            transition={{ delay: 0.5, duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
            className="bg-white rounded-xl border border-[#DCE4ED] p-3 flex items-center gap-3"
          >
            <GripVertical size={14} className="text-[#8DA0B9]" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#0D1B2A]">{name}</p>
            </div>
            {i === 1 && (
              <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1, repeat: Infinity }} className="text-xs text-[#00AEEF]">
                dragging...
              </motion.span>
            )}
          </motion.div>
        ))}
      </div>
    ),
    checkout: (
      <div className="max-w-[260px] mx-auto space-y-3">
        <div className="flex gap-2">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex-1 bg-white rounded-xl border border-[#DCE4ED] p-3 text-center">
            <Download size={20} className="text-[#1A3A64] mx-auto mb-1" />
            <p className="text-xs font-medium text-[#0D1B2A]">PDF</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex-1 bg-[#1A3A64] rounded-xl p-3 text-center">
            <CreditCard size={20} className="text-white mx-auto mb-1" />
            <p className="text-xs font-medium text-white">Checkout</p>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="bg-white rounded-xl border border-[#DCE4ED] p-3">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-[#8DA0B9]">3 coverages</span>
            <span className="text-[#0D1B2A] font-medium">$450/mo</span>
          </div>
          <div className="h-1.5 bg-[#E8EEF4] rounded-full overflow-hidden">
            <motion.div animate={{ width: ['0%', '100%'] }} transition={{ delay: 0.8, duration: 1.5 }} className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #00AEEF, #00C4AD, #83CF44)' }} />
          </div>
        </motion.div>
      </div>
    ),
    ready: (
      <div className="text-center">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Rocket size={64} className="text-[#00AEEF] mx-auto" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-4 flex justify-center gap-1"
        >
          {['#1A3A64', '#00AEEF', '#00C4AD', '#83CF44'].map((c, i) => (
            <motion.div
              key={c}
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ delay: i * 0.2, duration: 1, repeat: Infinity, repeatDelay: 1 }}
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: c }}
            />
          ))}
        </motion.div>
      </div>
    ),
  };

  return visuals[step.visual] || null;
};

const OnboardingWizard = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;
  const isFirst = currentStep === 0;

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(() => onComplete?.(), 300);
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    setTimeout(() => onComplete?.(), 300);
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
        data-testid="onboarding-wizard"
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-[#0D1B2A]/60 backdrop-blur-sm" />

        {/* Card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.3 }}
          className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        >
          {/* Skip button */}
          {!isLast && (
            <button
              onClick={handleSkip}
              className="absolute top-4 right-4 p-2 rounded-lg text-[#8DA0B9] hover:text-[#0D1B2A] hover:bg-[#F0F4F8] transition-colors z-10"
              data-testid="onboarding-skip-btn"
            >
              <X size={18} />
            </button>
          )}

          {/* Visual area */}
          <div className="h-[200px] bg-gradient-to-br from-[#F0F4F8] to-[#E8EEF4] flex items-center justify-center p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(#1A3A64 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
            <StepVisual step={step} />
          </div>

          {/* Content */}
          <div className="p-6 pb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1A3A64, #00AEEF)' }}>
                <step.icon size={16} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step.title}
                </h2>
              </div>
            </div>
            <p className="text-sm text-[#00AEEF] font-medium mb-2">{step.subtitle}</p>
            <p className="text-sm text-[#3D5A80] leading-relaxed">{step.description}</p>

            {step.tip && (
              <div className="mt-3 px-3 py-2 bg-[#00AEEF]/5 border border-[#00AEEF]/15 rounded-lg">
                <p className="text-xs text-[#1A3A64]">
                  <span className="font-medium">Tip: </span>{step.tip}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6 flex items-center justify-between">
            {/* Progress */}
            <div className="flex gap-1.5">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: i === currentStep ? 24 : 8,
                    background: i <= currentStep
                      ? 'linear-gradient(90deg, #00AEEF, #00C4AD)'
                      : '#DCE4ED',
                  }}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              {!isFirst && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentStep(s => s - 1)}
                  className="border-[#DCE4ED]"
                  data-testid="onboarding-prev-btn"
                >
                  <ArrowLeft size={14} className="mr-1" />
                  Back
                </Button>
              )}
              {isLast ? (
                <Button
                  size="sm"
                  className="btn-gradient"
                  onClick={handleComplete}
                  data-testid="onboarding-done-btn"
                >
                  Start Building
                  <Rocket size={14} className="ml-1" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="btn-primary"
                  onClick={() => setCurrentStep(s => s + 1)}
                  data-testid="onboarding-next-btn"
                >
                  Next
                  <ArrowRight size={14} className="ml-1" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export { OnboardingWizard, STORAGE_KEY };
export default OnboardingWizard;
